import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Pagination, Button, Modal, Input, Spin, Select, Upload, Form } from 'antd';
import { ProPageHeader as PageHeader } from '@ant-design/pro-layout';
import { DeleteFilled, UploadOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getAd, removeAd, addAd, getSport } from '../../../store/slices/adminSlice';
import { CustomModal, SortableTableHeader } from '../../../components/common';
import { requiredRule } from '../../../utils';
import * as constant from '../../../actions/constant';

const { confirm } = Modal;
const { Option } = Select;

// TypeScript interfaces
interface AdItem {
  _id: string;
  name: string;
  type: string;
  mediaType: string;
  userType: string;
  sport: string;
  pages?: string;
  redirectUrl?: string;
}

// Static configurations
const MEDIA_TYPES = [
  { name: 'image' },
  { name: 'gif' },
  { name: 'html' },
  { name: 'json' },
];

const USER_TYPES = [
  { name: 'TopSport' },
  { name: 'TopTippers' },
];

const AD_TYPES = [
  { name: 'TopSport Banner', value: 'topsport_banner' },
  { name: 'Tipping Success', value: 'tipping_success' },
];

const PAGE_TYPES = [
  { name: 'TIPPING PAGE', value: 'Tip' },
  { name: 'SCORECARD PAGE', value: 'Scorecard' },
];

const FILTER_OPTIONS = [
  { name: 'TIPPING PAGE BANNER', value: 'Tip' },
  { name: 'SCORECARD PAGE BANNER', value: 'Scorecard' },
  { name: 'TIPPING SUCCESS', value: 'tipping_success' },
];

const TABLE_COLUMNS = [
  { key: 'name', label: 'Name', sortable: true, sortKey: 'name' },
  { key: 'type', label: 'Type', sortable: true, sortKey: 'type' },
  { key: 'mediaType', label: 'Media Type', sortable: true, sortKey: 'mediaType' },
  { key: 'userType', label: 'User Type', sortable: true, sortKey: 'type' },
  { key: 'sports', label: 'Sports', sortable: true, sortKey: 'type' },
  { key: 'pages', label: 'Pages', sortable: true, sortKey: 'type' },
  { key: 'action', label: 'Action', sortable: false },
];

const Ad: React.FC = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  // Redux state
  const { adData, adCount, sportData, loading: reduxLoading } = useAppSelector((state) => state.admin);

  // Local state
  const [currentPage, setCurrentPage] = useState(0);
  const [sortOrder, setSortOrder] = useState<number | ''>('');
  const [sortValue, setSortValue] = useState('');
  const [visible, setVisible] = useState(false);
  const [adId, setAdId] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [fileList, setFileList] = useState<File[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');

  // Fetch data on mount
  useEffect(() => {
    const params = { page: 0, sortValue: '', sortOrder: '' };
    dispatch(getAd(params));
    dispatch(getSport(params));
    setLoading(false);
  }, [dispatch]);

  // Handlers
  const fetchAds = useCallback((params: Record<string, any> = {}) => {
    dispatch(getAd({ page: currentPage, sortValue, sortOrder, filter: filterType, ...params }));
  }, [dispatch, currentPage, sortValue, sortOrder, filterType]);

  const handlePageChange = (page: number) => {
    const newPage = page - 1;
    setCurrentPage(newPage);
    fetchAds({ page: newPage });
  };

  const handleSort = (sortKey: string) => {
    const newOrder = sortOrder === -1 ? 1 : -1;
    setSortOrder(newOrder);
    setSortValue(sortKey);
    fetchAds({ sortValue: sortKey, sortOrder: newOrder });
  };

  const handleFilter = (value: string) => {
    setFilterType(value);
    dispatch(getAd({ filter: value }));
  };

  const showModal = (ad?: AdItem) => {
    setVisible(true);
    setAdId(ad?._id || '');
    setSelectedType(ad?.type || '');
    setFileList([]);

    if (ad) {
      setTimeout(() => {
        form.setFieldsValue({
          name: ad.name,
          type: ad.type,
          redirectUrl: ad.redirectUrl,
          mediaType: ad.mediaType,
          userType: ad.userType,
          sport: ad.sport,
          page: ad.pages,
        });
      }, 100);
    } else {
      form.resetFields();
    }
  };

  const handleCancel = () => {
    setVisible(false);
    setFileList([]);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const formData = new FormData();

      fileList.forEach((file) => formData.append('files', file));
      Object.entries({ ...values, adId }).forEach(([key, val]) => {
        if (val !== undefined) formData.append(key, val as string);
      });

      setVisible(false);
      setFileList([]);
      dispatch(addAd(formData));
    } catch {
      // Validation failed - form will show errors
    }
  };

  const showDeleteConfirm = (id: string) => {
    confirm({
      title: constant.DELETE_RECORD,
      content: '',
      onOk: () => dispatch(removeAd({ adId: id })),
    });
  };

  const handleTypeSelect = (value: string) => {
    setSelectedType(value);
    form.setFieldsValue({ mediaType: value === 'tipping_success' ? 'video' : '' });
  };

  const handleBeforeUpload = (file: File) => {
    setFileList([file]);
    return false;
  };

  // Helper to get display values
  const getTypeName = (typeValue: string) => AD_TYPES.find((t) => t.value === typeValue)?.name || typeValue;
  const getSportName = (sportId: string) => (sportData.find((s: any) => s._id === sportId) as any)?.sportname || '';

  // Memoized sport options
  const sportOptions = useMemo(() => (
    sportData.map((sport: any, index) => (
      <Option key={index} value={sport._id}>{sport.sportname || sport.name}</Option>
    ))
  ), [sportData]);

  // Render table header
  const renderTableHeader = () => (
    <tr>
      {TABLE_COLUMNS.map(({ key, label, sortable, sortKey }) =>
        sortable ? (
          <SortableTableHeader
            key={key}
            label={label}
            sortKey={sortKey || key}
            currentSortOrder={sortOrder}
            onSort={handleSort}
          />
        ) : (
          <th key={key} scope="col">{label}</th>
        )
      )}
    </tr>
  );

  // Render ad rows
  const renderAdRows = () =>
    adData.map((ad: AdItem, index: number) => (
      <tr key={ad._id || index}>
        <td>{ad.name}</td>
        <td>{getTypeName(ad.type)}</td>
        <td>{ad.mediaType}</td>
        <td>{ad.userType}</td>
        <td>{getSportName(ad.sport)}</td>
        <td>{ad.pages === 'Tip' ? 'TIPPING' : ad.pages}</td>
        <td className="action-icons">
          <Button type="link" className="btn-sm mr-2" onClick={() => showModal(ad)}>
            <i className="fa fa-pencil" style={{ color: '#666' }} />
          </Button>
          <Button type="link" className="btn-sm mr-2" onClick={() => showDeleteConfirm(ad._id)}>
            <DeleteFilled style={{ color: '#666' }} />
          </Button>
        </td>
      </tr>
    ));

  return (
    <div className="box box-default mb-4">
      <PageHeader
        prefixedClassName="ant-page-header"
        title="AD"
        subTitle=""
        extra={[
          <Select
            key="type-filter"
            placeholder="Type"
            style={{ width: 150 }}
            className="mobile-bottom-space"
            onChange={handleFilter}
          >
            <Option value="">All</Option>
            {FILTER_OPTIONS.map((opt, idx) => (
              <Option key={idx} value={opt.value}>{opt.name}</Option>
            ))}
          </Select>,
          <Button key="add" type="default" onClick={() => showModal()}>
            Add Ad
          </Button>,
        ]}
      />
      <Spin spinning={loading || reduxLoading} delay={500}>
        <div className="box-body">
          <div className="table-responsive">
            <table className="table">
              <thead>{renderTableHeader()}</thead>
              <tbody>{renderAdRows()}</tbody>
            </table>
          </div>
          <Pagination
            style={{ textAlign: 'right' }}
            pageSize={10}
            current={currentPage + 1}
            total={adCount || 1}
            onChange={handlePageChange}
          />
          <CustomModal
            handleCancel={handleCancel}
            handleOk={handleOk}
            visible={visible}
            title={adId ? 'Edit Ad' : 'Add Ad'}
            backBtnText="Back"
            submitBtnText="Submit"
          >
            <Form form={form} className="sport-form l_hgt0" layout="vertical">
              <Form.Item label="Name" name="name" rules={[requiredRule('name')]}>
                <Input placeholder="name" />
              </Form.Item>
              <Form.Item label="Select Type" name="type" rules={[requiredRule('type')]}>
                <Select style={{ width: 200 }} onChange={handleTypeSelect}>
                  {AD_TYPES.map((x, index) => (
                    <Option key={index} value={x.value}>{x.name}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="Select User Type" name="userType" rules={[requiredRule('user type')]}>
                <Select style={{ width: 200 }}>
                  {USER_TYPES.map((x, index) => (
                    <Option key={index} value={x.name}>{x.name}</Option>
                  ))}
                </Select>
              </Form.Item>
              {selectedType === 'topsport_banner' && (
                <Form.Item label="Select Page Type" name="page" rules={[requiredRule('page type')]}>
                  <Select style={{ width: 200 }}>
                    {PAGE_TYPES.map((x, index) => (
                      <Option key={index} value={x.value}>{x.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
              <Form.Item label="Select Sports Name" name="sport" rules={[requiredRule('sports type')]}>
                <Select style={{ width: 200 }}>{sportOptions}</Select>
              </Form.Item>
              <Form.Item label="Select Media Type" name="mediaType" rules={[requiredRule('media type')]}>
                <Select>
                  {MEDIA_TYPES.map((x, index) => (
                    <Option key={index} value={x.name}>{x.name}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="Redirect Url" name="redirectUrl" rules={[requiredRule('redirect url')]}>
                <Input placeholder="Redirect Url" />
              </Form.Item>
              <Upload
                name="file"
                showUploadList={fileList.length > 0}
                beforeUpload={handleBeforeUpload}
              >
                <Button disabled={fileList.length > 0}>
                  <UploadOutlined /> Click to Upload
                </Button>
              </Upload>
            </Form>
          </CustomModal>
        </div>
      </Spin>
    </div>
  );
};

export default Ad;



